import { useEffect, useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Trash2, Search, Sparkles, ShieldOff, Tag, Stethoscope, Syringe, Bandage } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { confirm } from "@/lib/confirm";

/**
 * Centralized management of fuzzy-match learning data:
 *  - Aliases that the system has accepted as valid variations
 *  - Rejection lists ("Não é da equipe" / "Não é o mesmo")
 *
 * Lets the admin undo a single learning event without touching the entity.
 */
export default function AliasesPage() {
  const [tab, setTab] = useState("surgeons");
  return (
    <div className="space-y-6" data-testid="aliases-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Aprendizado de Nomes</h1>
        <p className="text-sm text-slate-500 mt-1">
          Apelidos aceitos pelo sistema e bloqueios "Não é da equipe / Não é o mesmo".
          Use para desfazer aprendizados errados ou auditar o que o filtro está fazendo nos seus mapas.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="surgeons" data-testid="tab-aliases-surgeons">
            <Stethoscope className="h-3.5 w-3.5 mr-1" /> Cirurgiões
          </TabsTrigger>
          <TabsTrigger value="anesths" data-testid="tab-aliases-anesths">
            <Syringe className="h-3.5 w-3.5 mr-1" /> Anestesistas
          </TabsTrigger>
          <TabsTrigger value="procedures" data-testid="tab-aliases-procedures">
            <Bandage className="h-3.5 w-3.5 mr-1" /> Procedimentos
          </TabsTrigger>
          <TabsTrigger value="blocks" data-testid="tab-aliases-blocks">
            <ShieldOff className="h-3.5 w-3.5 mr-1" /> Bloqueios
          </TabsTrigger>
        </TabsList>

        <TabsContent value="surgeons">
          <PersonAliases endpoint="/surgeons" entityType="surgeon" testidPrefix="surg" />
        </TabsContent>
        <TabsContent value="anesths">
          <PersonAliases endpoint="/anesthesiologists" entityType="anesthesiologist" testidPrefix="anest" />
        </TabsContent>
        <TabsContent value="procedures">
          <ProcedureAliases />
        </TabsContent>
        <TabsContent value="blocks">
          <Blocks />
        </TabsContent>
      </Tabs>
    </div>
  );
}


function PersonAliases({ endpoint, entityType, testidPrefix }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  async function load() {
    try {
      const r = await api.get(endpoint);
      setItems(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }
  useEffect(() => { load(); }, [endpoint]);

  const rows = useMemo(() => {
    const out = [];
    for (const p of items) {
      const aliases = p.name_aliases || [];
      if (!aliases.length) continue;
      out.push({ id: p.id, name: p.full_name, aliases });
    }
    if (!q.trim()) return out;
    const ql = q.toLowerCase();
    return out.filter((r) =>
      r.name.toLowerCase().includes(ql) || r.aliases.some((a) => a.toLowerCase().includes(ql))
    );
  }, [items, q]);

  async function removeAlias(id, alias) {
    if (!(await confirm({ description: `Remover apelido "${alias}"?\n\nO sistema voltará a tratar esse nome como NÃO sendo essa pessoa.` }))) return;
    try {
      await api.delete(`${endpoint}/${id}/aliases?alias=${encodeURIComponent(alias)}`);
      toast.success("Apelido removido");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  return (
    <Card className="border-slate-200">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-slate-400" />
          <Input
            placeholder="Buscar nome ou apelido..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="max-w-sm"
            data-testid={`${testidPrefix}-aliases-search`}
          />
          <span className="text-xs text-slate-500 ml-auto">{rows.length} pessoa{rows.length !== 1 ? "s" : ""} com apelidos</span>
        </div>

        {rows.length === 0 && (
          <div className="text-sm text-slate-500 py-12 text-center border border-dashed border-slate-200 rounded-md">
            <Sparkles className="h-6 w-6 text-slate-300 mx-auto mb-2" />
            Nenhum apelido aprendido ainda.<br />
            Toda vez que você confirma "É o mesmo" no banner do mapa filtrado, o nome alternativo é guardado aqui.
          </div>
        )}

        <div className="divide-y divide-slate-100">
          {rows.map((r) => (
            <div key={r.id} className="py-3 flex items-start gap-3 flex-wrap" data-testid={`${testidPrefix}-row-${r.id}`}>
              <div className="font-medium text-slate-900 min-w-[200px]">{r.name}</div>
              <div className="flex items-center gap-1.5 flex-wrap flex-1">
                {r.aliases.map((a) => (
                  <Badge
                    key={a}
                    variant="outline"
                    className="px-2 py-1 bg-emerald-50 border-emerald-200 text-emerald-900 hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700 cursor-pointer transition-colors group"
                    onClick={() => removeAlias(r.id, a)}
                    title="Clique para remover este apelido"
                    data-testid={`${testidPrefix}-alias-${a.replace(/\s+/g, "-")}`}
                  >
                    <Tag className="h-3 w-3 mr-1" />
                    {a}
                    <Trash2 className="h-3 w-3 ml-1.5 opacity-40 group-hover:opacity-100" />
                  </Badge>
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}


function ProcedureAliases() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  async function load() {
    try {
      const r = await api.get("/procedures");
      setItems(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    const out = [];
    for (const p of items) {
      const aliases = p.aliases || p.name_aliases || [];
      if (!aliases.length) continue;
      out.push({ id: p.id, name: p.name, aliases });
    }
    if (!q.trim()) return out;
    const ql = q.toLowerCase();
    return out.filter((r) =>
      r.name.toLowerCase().includes(ql) || r.aliases.some((a) => a.toLowerCase().includes(ql))
    );
  }, [items, q]);

  async function removeAlias(id, alias) {
    if (!(await confirm({ description: `Remover apelido "${alias}"?` }))) return;
    try {
      await api.delete(`/procedures/${id}/aliases?alias=${encodeURIComponent(alias)}`);
      toast.success("Apelido removido");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  return (
    <Card className="border-slate-200">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-slate-400" />
          <Input placeholder="Buscar procedimento..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" data-testid="proc-aliases-search" />
          <span className="text-xs text-slate-500 ml-auto">{rows.length} procedimento{rows.length !== 1 ? "s" : ""}</span>
        </div>
        {rows.length === 0 && (
          <div className="text-sm text-slate-500 py-12 text-center border border-dashed border-slate-200 rounded-md">
            <Sparkles className="h-6 w-6 text-slate-300 mx-auto mb-2" />
            Nenhuma variação aprendida ainda.
          </div>
        )}
        <div className="divide-y divide-slate-100">
          {rows.map((r) => (
            <div key={r.id} className="py-3 flex items-start gap-3 flex-wrap" data-testid={`proc-row-${r.id}`}>
              <div className="font-medium text-slate-900 min-w-[280px]">{r.name}</div>
              <div className="flex items-center gap-1.5 flex-wrap flex-1">
                {r.aliases.map((a) => (
                  <Badge
                    key={a}
                    variant="outline"
                    className="px-2 py-1 bg-blue-50 border-blue-200 text-blue-900 hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700 cursor-pointer transition-colors"
                    onClick={() => removeAlias(r.id, a)}
                    title="Clique para remover"
                  >
                    <Tag className="h-3 w-3 mr-1" />
                    {a}
                    <Trash2 className="h-3 w-3 ml-1.5 opacity-40" />
                  </Badge>
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}


function Blocks() {
  const [nameRejects, setNameRejects] = useState([]);
  const [procRejects, setProcRejects] = useState([]);
  const [people, setPeople] = useState({}); // entity_id -> full_name

  async function load() {
    try {
      const [n, p, surg, anesth] = await Promise.all([
        api.get("/name-match-rejects"),
        api.get("/procedures/variation-rejects"),
        api.get("/surgeons"),
        api.get("/anesthesiologists"),
      ]);
      setNameRejects(n.data || []);
      setProcRejects(p.data || []);
      const map = {};
      [...(surg.data || []), ...(anesth.data || [])].forEach((x) => { map[x.id] = x.full_name; });
      setPeople(map);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }
  useEffect(() => { load(); }, []);

  async function unrejectName(item) {
    if (!(await confirm({ description: `Desfazer bloqueio de "${item.raw_name}"?\n\nO sistema voltará a sugerir essa associação no filtro.` }))) return;
    try {
      const params = new URLSearchParams({ raw_name: item.raw_name });
      if (item.entity_id) {
        params.set("entity_id", item.entity_id);
        params.set("entity_type", item.entity_type || "surgeon");
      }
      await api.delete(`/name-match-rejects?${params.toString()}`);
      toast.success("Bloqueio removido");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function unrejectProc(item) {
    if (!(await confirm({ description: `Desfazer bloqueio de "${item.raw_name}"?` }))) return;
    try {
      await api.delete(`/procedures/variation-rejects?raw_name=${encodeURIComponent(item.raw_name)}&procedure_id=${item.procedure_id}`);
      toast.success("Bloqueio removido");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  const globalRejects = nameRejects.filter((r) => !r.entity_id);
  const pairRejects = nameRejects.filter((r) => !!r.entity_id);

  return (
    <div className="space-y-4">
      {/* Global "não é da equipe" */}
      <Card className="border-slate-200">
        <CardContent className="p-5">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-1">
            <ShieldOff className="h-4 w-4 text-rose-700" /> "Não é da equipe" — bloqueios globais
          </h3>
          <p className="text-xs text-slate-500 mb-4">
            Nomes que você marcou como NÃO sendo da sua equipe. O sistema não tentará mais reconhecê-los nem promovê-los.
          </p>
          {globalRejects.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">Nenhum bloqueio global.</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {globalRejects.map((r) => (
                <Badge
                  key={r.raw_name_norm}
                  variant="outline"
                  className="px-2 py-1 bg-rose-50 border-rose-200 text-rose-900 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 cursor-pointer transition-colors"
                  onClick={() => unrejectName(r)}
                  title="Clique para desfazer"
                  data-testid={`global-block-${r.raw_name_norm.replace(/\s+/g, "-")}`}
                >
                  {r.raw_name}
                  <Trash2 className="h-3 w-3 ml-1.5 opacity-40" />
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pair rejects */}
      <Card className="border-slate-200">
        <CardContent className="p-5">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-1">
            <ShieldOff className="h-4 w-4 text-amber-700" /> "Não é o mesmo" — bloqueios por pessoa
          </h3>
          <p className="text-xs text-slate-500 mb-4">
            O sistema sugeriu que um nome era variação de uma pessoa do cadastro, e você corrigiu. Bloqueia apenas essa associação.
          </p>
          {pairRejects.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">Nenhum bloqueio por pessoa.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {pairRejects.map((r) => (
                <div key={`${r.entity_id}-${r.raw_name_norm}`} className="py-2 flex items-center gap-3" data-testid={`pair-block-${r.raw_name_norm.replace(/\s+/g, "-")}`}>
                  <span className="font-medium text-slate-900">{r.raw_name}</span>
                  <span className="text-xs text-slate-500">≠</span>
                  <span className="text-sm text-slate-700">{people[r.entity_id] || r.entity_id?.slice(0, 8)}</span>
                  <Button size="sm" variant="ghost" className="h-7 text-xs ml-auto text-rose-700 hover:bg-rose-50" onClick={() => unrejectName(r)}>
                    <Trash2 className="h-3 w-3 mr-1" /> Desfazer
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Procedure rejects */}
      <Card className="border-slate-200">
        <CardContent className="p-5">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-1">
            <ShieldOff className="h-4 w-4 text-blue-700" /> Procedimentos — variações rejeitadas
          </h3>
          <p className="text-xs text-slate-500 mb-4">
            Variações de nomes de procedimentos que você recusou no banner do mapa filtrado.
          </p>
          {procRejects.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">Nenhuma variação rejeitada.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {procRejects.map((r) => (
                <div key={`${r.procedure_id}-${r.raw_name_norm}`} className="py-2 flex items-center gap-3" data-testid={`proc-block-${r.raw_name_norm.replace(/\s+/g, "-")}`}>
                  <span className="font-medium text-slate-900">{r.raw_name}</span>
                  <span className="text-xs text-slate-500">≠</span>
                  <span className="text-sm text-slate-700">{r.procedure_name || r.procedure_id?.slice(0, 8)}</span>
                  <Button size="sm" variant="ghost" className="h-7 text-xs ml-auto text-rose-700 hover:bg-rose-50" onClick={() => unrejectProc(r)}>
                    <Trash2 className="h-3 w-3 mr-1" /> Desfazer
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
